import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { AdvancedDynamicTexture } from '@babylonjs/gui/2D/advancedDynamicTexture';
import { TextBlock } from '@babylonjs/gui/2D/controls/textBlock';
import { Rectangle } from '@babylonjs/gui/2D/controls/rectangle';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import remove from 'lodash/remove';
import clone from 'lodash/clone';

import { loadFont } from '../../../../Utils/font';
import { SystemLayer } from '../../../System/systemLayer';
import {
    PatternDecal, IDecalParameters, IVariantDecal
} from './patternDecal';

export interface IVariantText extends IVariantDecal {
    material: string,
    text: string,
    font?: string,
}

export interface ITextParameters {
    defaultText?: string,
    maximumCharacter?: number,
    fontSize?: number,
    fonts?: string[],
}

export interface ITextGeometry extends ITextParameters, IDecalParameters { }

export const DEFAULT_TEXT_GEOMETRY = {
    type: 'text',
    position: Vector3.Zero(),
    normal: Vector3.Zero(),
    rotation: 0,
    size: 1,
    sourceMeshStayVisible: true,
    maximumCharacter: 10,
    fontSize: 1,
    fonts: [],
};

export class PatternText extends PatternDecal {
    private opacityTexture: AdvancedDynamicTexture

    private metallicTexture: AdvancedDynamicTexture

    private textContainer: Rectangle

    private textBlock: TextBlock

    // private outlineMesh: AbstractMesh

    protected currentVariant: IVariantText = {
        name: '',
        meshes: [],
        type: 'text',
        text: '',
        material: ''
    }

    protected textGeometry: ITextParameters = {}

    constructor(systemLayer: SystemLayer, meshes: string[], geometry: ITextGeometry) {
        super(systemLayer, meshes);

        this.currentVariant.name = geometry.name;
        this.currentVariant.meshes = meshes;
        this.material.opacityTexture = this.getOpacityTexture(0);
        this.material.opacityTexture.getAlphaFromRGB = true;
        this.material.metallicTexture = this.getMetallicTexture();
        this.setGeometry(geometry);
    }

    private setTextParameters(geometry: ITextParameters) {
        if (geometry.defaultText) this.setText(geometry.defaultText, true);
        if (geometry.maximumCharacter) this.setMaximumCharacter(geometry.maximumCharacter);
        if (geometry.fontSize) this.setFontSize(geometry.fontSize);
        if (geometry.fonts) this.setFonts(geometry.fonts);
    }

    public setGeometry(geometry: ITextGeometry): void {
        this.setTextParameters(geometry);
        this.setDecalParameters(geometry);
    }

    public getGeometry(): ITextGeometry {
        return { ...this.textGeometry, ...this.geometry };
    }

    public resetGeometry(): void {
        this.setGeometry({ ...this.textGeometry, ...this.geometry });
    }

    protected highlight(highlight: boolean): void {
        // const { detailMap } = this.material;
        if (highlight) {
            this.textContainer.thickness = 5;
            // detailMap.texture = this.getOpacityTexture(50);
            // detailMap.isEnabled = true;
            // detailMap.diffuseBlendLevel = 0.1; // between 0 and 1
            // detailMap.bumpLevel = 1; // between 0 and 1
            // detailMap.roughnessBlendLevel = 0.25; // between 0 and 1
        } else {
            this.textContainer.thickness = 0;
            // detailMap.texture = null;
            // detailMap.isEnabled = false;
        }
    }

    private getOpacityTexture(outline: number): AdvancedDynamicTexture {
        this.opacityTexture = this.getAdvanceTexture();

        this.textContainer = new Rectangle();
        this.textContainer.background = 'black';
        this.textContainer.thickness = 0;
        this.textContainer.adaptHeightToChildren = true;
        this.textContainer.adaptWidthToChildren = true;
        this.opacityTexture.addControl(this.textContainer);

        // Not working
        // this.textContainer.isPointerBlocker = true;
        // this.textContainer.onPointerDownObservable.add(() => {
        //     console.log('click');
        // });

        this.textBlock = new TextBlock();
        this.textBlock.color = 'white';
        this.textBlock.outlineWidth = outline;
        this.textBlock.outlineColor = 'white';
        this.textBlock.resizeToFit = true; // Or adaptToChildren doesn't work
        this.textBlock.fontSize = 50 * window.devicePixelRatio;
        this.textBlock.fontFamily = 'Helvetica Neue';
        this.textContainer.addControl(this.textBlock);

        // this.textContainer.background = 'rgb(0,255,0, 1)';
        return this.opacityTexture;
    }

    private getMetallicTexture(): AdvancedDynamicTexture {
        this.metallicTexture = this.getAdvanceTexture();
        const whiteContainer = new Rectangle();
        whiteContainer.background = 'white';
        whiteContainer.thickness = 0;
        this.metallicTexture.addControl(whiteContainer);
        return this.metallicTexture;
    }

    protected textureSize = 2000

    private getAdvanceTexture(): AdvancedDynamicTexture {
        const advancedTexture = new AdvancedDynamicTexture('text_texture', this.textureSize, this.textureSize, this._system.scene);
        advancedTexture.hasAlpha = true;
        // advancedTexture.renderScale = window.devicePixelRatio;
        advancedTexture.wrapU = Texture.CLAMP_ADDRESSMODE;
        advancedTexture.wrapV = Texture.CLAMP_ADDRESSMODE;
        return advancedTexture;
    }

    public setMaximumCharacter(maximumCharacter: number): void {
        this.textGeometry.maximumCharacter = maximumCharacter;
        this.setText(this.currentVariant.text);
    }

    public setText(text: string, defaultText?: boolean): string {
        if (!text) text = '';
        if (this.textGeometry.maximumCharacter) {
            text = text.substring(0, this.textGeometry.maximumCharacter);
        }
        if (this.textBlock) this.textBlock.text = text;
        this.updateDecal();
        this.currentVariant.text = text;
        if (defaultText) this.textGeometry.defaultText = text;
        if (!this.sourceMesh) return text;
        if (this.geometry.sourceMeshStayVisible === false) {
            if (text) {
                this.sourceMesh.isVisible = false;
                this.mesh.isVisible = true;
            } else {
                this.sourceMesh.isVisible = true;
                this.mesh.isVisible = false;
            }
        }
        return text;
    }

    public setFontSize(fontSize: number): void {
        this.textGeometry.fontSize = fontSize;
        if (this.textBlock) this.textBlock.fontSize = 50 * window.devicePixelRatio * fontSize;
        this.updateDecal();
    }

    public setFonts(fonts: string[]): void {
        this.textGeometry.fonts = fonts;
        fonts.forEach((font) => {
            loadFont(font, () => {
                if (font === fonts[0]) this.setFont(font);
            });
        });
    }

    public getFonts(): string[] {
        return [...this.textGeometry.fonts];
    }

    public addFont(font: string): string[] {
        const { fonts } = this.textGeometry;
        if (!fonts.includes(font)) {
            loadFont(font);
            this.textGeometry.fonts.push(font);
        }
        return clone(this.textGeometry.fonts);
    }

    public removeFont(font: string): string[] {
        remove(this.textGeometry.fonts, (f) => font === f);
        return clone(this.textGeometry.fonts);
    }

    public setFont(font: string): void {
        this.currentVariant.font = font;
        if (this.textBlock) this.textBlock.fontFamily = font;
        this.updateDecal();
    }

    public setMaterial(material: string): void {
        this.currentVariant.material = material;
        this._setMaterial(material);
        this.updateDecal();
    }

    public setVariant(variantText: IVariantText): void {
        if (variantText.text !== undefined) this.setText(variantText.text);
        if (variantText.font !== undefined) this.setFont(variantText.font);
        if (variantText.material !== undefined) this.setMaterial(variantText.material);
    }

    public resetVariant(): void {
        this.setText('');
    }
}
